import { Box, Dropdown, Menu, MenuButton, MenuItem } from "@mui/joy";
import { ApiUserInline } from "griptonite-api-client";
import { useEffect, useMemo, useState } from "react";

import { useVenueCompetitionsByUuid } from "@/hooks/competitions";

import { useGradesQuery } from "../../hooks/grades";
import { useVenueRouteStatsByUuid } from "../../hooks/routes";
import { useCurrentVenueQuery } from "../../hooks/venues";
import { getGraphData, usePresets } from "../Analyse/Analyse";
import AnalyseGraph from "../Analyse/AnalyseGraph";
import {
  BreakdownId,
  DimensionId,
  dimensions,
  getBreakdownDimensionId,
} from "../Analyse/dimensions";
import { MetricId } from "../Analyse/metrics";
import { useArchiveQueryContext } from "../Archive/ArchiveRoutesQueryContext";
import { useRoutesTableStore } from "../RoutesTable/RoutesProvider2";
import { ArchiveAnalysisTypeSelector } from "./ArchiveGraphs";

const emptyArray = [] as never[];
const emptyObject = {} as Record<string, never>;

const useData = ({
  axis,
  breakdown,
  metric,
  sortByMetric,
}: {
  axis: DimensionId;
  breakdown: BreakdownId | null;
  metric: MetricId;
  sortByMetric: boolean | undefined;
}) => {
  // Make sure we load in grades if they're not present already
  useGradesQuery();

  const { data: venue } = useCurrentVenueQuery();

  const archiveQuery = useArchiveQueryContext();

  const routes = archiveQuery.data?.routes;

  const { isFetched: isGradesLoaded } = useGradesQuery();

  // const circuits = useVenueCircuitsByUuid(venue?.uuid);
  const { data: routeStats } = useVenueRouteStatsByUuid(venue?.uuid);
  const competitions = useVenueCompetitionsByUuid(venue?.uuid, {
    allPages: true,
  });

  // const setters = useVenueRouteSettersByUuid(venue?.uuid);
  // const zones = useVenueZonesByUuid(venue?.uuid);
  // const lines = useVenueLinesByUuid(venue?.uuid);

  const { circuits, zones, lines, setters } = useMemo(() => {
    const circuits = {} as Record<string, { name: string; uuid: string; colors: string[] }>;
    const lines = {} as Record<string, { name: string; uuid: string; canLead: boolean }>;
    const zones = {} as Record<string, { name: string; uuid: string }>;
    const setters = {} as Record<string, ApiUserInline>;
    routes?.forEach((route) => {
      if (route.circuit) {
        if (!circuits[route.circuit.uuid]) {
          circuits[route.circuit.uuid] = {
            uuid: route.circuit.uuid,
            name: route.circuit.name || "",
            colors: route.circuit.colors ?? ["#FFFFFF"],
          };
        }
      }
      if (route.startLine) {
        if (!lines[route.startLine.uuid]) {
          lines[route.startLine.uuid] = {
            uuid: route.startLine.uuid,
            name: route.startLine.name,
            canLead: route.startLine.canLead,
          };
        }
      }
      if (route.zone) {
        if (!zones[route.zone.uuid]) {
          zones[route.zone.uuid] = {
            uuid: route.zone.uuid,
            name: route.zone.name,
          };
        }
      }
      route.setters.forEach((setter) => {
        if (!setters[setter.uuid]) {
          setters[setter.uuid] = setter;
        }
      });
    });

    return { circuits, zones, lines, setters };
  }, [routes]);

  const hasNextPage = archiveQuery.hasNextPage;
  const fetchNextPage = archiveQuery.fetchNextPage;
  const isFetchingNextPage = archiveQuery.isFetchingNextPage;

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage, isFetchingNextPage]);

  return useMemo(
    () =>
      getGraphData({
        routes: isGradesLoaded && routes ? routes : emptyArray,
        bag: {
          circuits: circuits || emptyObject,
          venue,
          routeStats: routeStats || emptyObject,
          zones: zones || emptyObject,
          lines: lines || emptyObject,
          setters: setters || emptyObject,
          competitions: competitions || emptyObject,
        },
        axis,
        breakdown,
        metric,
        sortByMetric,
      }),
    [
      isGradesLoaded,
      routes,
      circuits,
      venue,
      routeStats,
      zones,
      lines,
      setters,
      competitions,
      axis,
      breakdown,
      metric,
      sortByMetric,
    ],
  );
};

export default function ArchiveBreakdown({
  presetId,
}: {
  presetId: "gradeBalance" | "traitBalance";
}) {
  const setHighlightedRouteUuids = useRoutesTableStore((state) => state.setHighlightedRouteUuids);
  const [axis, setAxis] = useState<DimensionId>("grade");
  const [metric, setMetric] = useState<MetricId>("count");
  const [breakdown, setBreakdown] = useState<BreakdownId | null>("circuit");
  const [sortByMetric, setSortByMetric] = useState<boolean | undefined>(undefined);

  const presetChoices = usePresets();
  const selectedPreset = useMemo(() => {
    return presetChoices.find((x) => x.id === presetId);
  }, [presetId, presetChoices]);

  useEffect(() => {
    const preset = presetChoices.find((x) => x.id === presetId);
    if (preset) {
      if ("axis" in preset) {
        setAxis(preset.axis);
      } else {
        setAxis(preset.defaultAxis);
      }
      setMetric(preset.metric);
      if ("breakdown" in preset) {
        setBreakdown(preset.breakdown);
      } else {
        setBreakdown(preset.breakdownDefault);
      }
      setSortByMetric("sortByMetric" in preset ? preset.sortByMetric : undefined);
    }
  }, [selectedPreset]);

  const handleSelectAxis = (id: DimensionId) => {
    setAxis(id);
    if (selectedPreset?.id === "routePopularity") {
      setSortByMetric(id === "route");
    }
    if (breakdown === id) {
      setBreakdown(null);
    }
  };

  const availableBreakdowns = useMemo(() => {
    if (
      !selectedPreset ||
      ((!("breakdownChoices" in selectedPreset) || selectedPreset.breakdownChoices === undefined) &&
        (!("breakdown" in selectedPreset) || selectedPreset.breakdown === undefined))
    ) {
      return dimensions;
    }
    if ("breakdownChoices" in selectedPreset && selectedPreset.breakdownChoices) {
      return Object.fromEntries(selectedPreset.breakdownChoices.map((id) => [id, dimensions[id]]));
    } else if ("breakdown" in selectedPreset) {
      return Object.fromEntries(
        Object.entries(dimensions).filter(([id]) => {
          return id === selectedPreset.breakdown;
        }),
      );
    } else {
      throw new Error("Invalid preset");
    }
  }, [selectedPreset]);

  const selectedBreakdownId = getBreakdownDimensionId(breakdown);
  const selectedBreakdown = selectedBreakdownId
    ? availableBreakdowns[selectedBreakdownId]
    : undefined;

  const availableAxes = useMemo(() => {
    if (
      !selectedPreset ||
      ((!("axisChoices" in selectedPreset) || selectedPreset.axisChoices === undefined) &&
        (!("axis" in selectedPreset) || selectedPreset.axis === undefined))
    ) {
      return dimensions;
    }
    if ("axisChoices" in selectedPreset && selectedPreset.axisChoices) {
      return Object.fromEntries(selectedPreset.axisChoices.map((id) => [id, dimensions[id]]));
    } else if ("axis" in selectedPreset) {
      return Object.fromEntries(
        Object.entries(dimensions).filter(([id]) => {
          return id === selectedPreset.axis;
        }),
      );
    } else {
      throw new Error("Invalid preset");
    }
  }, [selectedPreset]);

  const data = useData({ axis, breakdown, metric, sortByMetric });

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex", gap: 1, margin: 1 }}>
        {/* <Dropdown>
          <MenuButton size="sm">Axis: {dimensions[axis].label}</MenuButton>
          <Menu>
            {Object.entries(dimensions).map(([id, { label }]) => (
              <MenuItem onClick={() => handleSelectAxis(id as MetricId)}>
                {label}
              </MenuItem>
            ))}
          </Menu>
        </Dropdown>
        <Dropdown>
          <MenuButton size="sm">
            Breakdown: {breakdown ? dimensions[breakdown].label : "None"}
          </MenuButton>
          <Menu>
            <MenuItem onClick={() => setBreakdown(null)}>None</MenuItem>
            {Object.entries(dimensions).map(([id, { label }]) =>
              id !== axis ? (
                <MenuItem onClick={() => setBreakdown(id as MetricId)}>
                  {label}
                </MenuItem>
              ) : null
            )}
          </Menu>
        </Dropdown> */}
        <ArchiveAnalysisTypeSelector />
        <Box sx={{ flex: 1 }}></Box>
        {Object.keys(availableAxes).length > 1 ? (
          <Dropdown>
            <MenuButton size="sm">
              {availableAxes[axis] ? `Axis: ${availableAxes[axis].label}` : "Axis"}
            </MenuButton>
            <Menu>
              {Object.entries(availableAxes).map(([id, { label }]) => (
                <MenuItem key={id} onClick={() => handleSelectAxis(id as DimensionId)}>
                  {label}
                </MenuItem>
              ))}
            </Menu>
          </Dropdown>
        ) : null}
        {Object.keys(availableBreakdowns).length > 1 ? (
          <Dropdown>
            <MenuButton size="sm">
              {selectedBreakdown ? `Breakdown: ${selectedBreakdown.label}` : "Breakdown"}
            </MenuButton>
            <Menu>
              {Object.entries(availableBreakdowns).map(([id, { label }]) =>
                id !== axis ? (
                  <MenuItem
                    key={id}
                    onClick={() => setBreakdown(id as DimensionId)}
                    selected={breakdown === id}
                  >
                    {label}
                  </MenuItem>
                ) : null,
              )}
            </Menu>
          </Dropdown>
        ) : null}
      </Box>
      <Box sx={{ flex: 1, overflow: "hidden" }}>
        <AnalyseGraph
          data={data}
          axis={axis}
          breakdown={breakdown}
          metric={metric}
          onColumnClick={(routeUuids) => setHighlightedRouteUuids(routeUuids)}
        />
      </Box>
    </Box>
  );
}
